import React, { useState } from 'react';
import { useToast } from '../../../components/ui/Toast';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const ReviewSchemaForm = ({ reviewCriteria, onReviewCriteriaChange, errors }) => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('conferences');
  const k = (key, opts) => t(`setupWizard.reviewSchema.${key}`, opts);

  const [newQuestion, setNewQuestion] = useState({
    questionText: '',
    questionType: 'scale',
    helpText: '',
    isRequired: true,
    scaleMin: 1,
    scaleMax: 5,
    scaleMinLabel: '',
    scaleMaxLabel: ''
  });

  const questionTypeOptions = ['scale', 'true_false', 'short_text', 'long_text']?.map(value => ({
    value,
    label: k(`questionType.options.${value}`)
  }));

  const defaultQuestions = [
    {
      questionText: k('defaults.quality.questionText'),
      questionType: 'scale',
      helpText: k('defaults.quality.helpText'),
      isRequired: true,
      scaleMin: 1,
      scaleMax: 5,
      scaleMinLabel: k('defaults.quality.minLabel'),
      scaleMaxLabel: k('defaults.quality.maxLabel')
    },
    {
      questionText: k('defaults.originality.questionText'),
      questionType: 'scale',
      helpText: k('defaults.originality.helpText'),
      isRequired: true,
      scaleMin: 1,
      scaleMax: 5,
      scaleMinLabel: k('defaults.originality.minLabel'),
      scaleMaxLabel: k('defaults.originality.maxLabel')
    },
    {
      questionText: k('defaults.clarity.questionText'),
      questionType: 'scale',
      helpText: k('defaults.clarity.helpText'),
      isRequired: true,
      scaleMin: 1,
      scaleMax: 5,
      scaleMinLabel: k('defaults.clarity.minLabel'),
      scaleMaxLabel: k('defaults.clarity.maxLabel')
    },
    {
      questionText: k('defaults.relevance.questionText'),
      questionType: 'true_false',
      helpText: k('defaults.relevance.helpText'),
      isRequired: true
    }
  ];

  const handleAddQuestion = () => {
    if (!newQuestion?.questionText?.trim()) {
      toastError(k('questionRequired'));
      return;
    }

    const question = {
      id: Date.now(),
      questionText: newQuestion?.questionText,
      questionType: newQuestion?.questionType,
      helpText: newQuestion?.helpText,
      isRequired: newQuestion?.isRequired,
      displayOrder: reviewCriteria?.length,
      scaleMin: newQuestion?.questionType === 'scale' ? newQuestion?.scaleMin : null,
      scaleMax: newQuestion?.questionType === 'scale' ? newQuestion?.scaleMax : null,
      scaleMinLabel: newQuestion?.questionType === 'scale' ? newQuestion?.scaleMinLabel : null,
      scaleMaxLabel: newQuestion?.questionType === 'scale' ? newQuestion?.scaleMaxLabel : null
    };

    onReviewCriteriaChange([...reviewCriteria, question]);
    setNewQuestion({
      questionText: '',
      questionType: 'scale',
      helpText: '',
      isRequired: true,
      scaleMin: 1,
      scaleMax: 5,
      scaleMinLabel: '',
      scaleMaxLabel: ''
    });
  };

  const handleRemoveQuestion = (questionId) => {
    onReviewCriteriaChange(reviewCriteria?.filter(q => q?.id !== questionId));
  };

  const handleMoveQuestion = (index, direction) => {
    const newQuestions = [...reviewCriteria];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newQuestions?.length) return;

    [newQuestions[index], newQuestions[targetIndex]] = [newQuestions?.[targetIndex], newQuestions?.[index]];
    newQuestions?.forEach((question, idx) => question.displayOrder = idx);

    onReviewCriteriaChange(newQuestions);
  };

  const handleUseDefaults = () => {
    const questions = defaultQuestions?.map((question, index) => ({
      id: Date.now() + index,
      ...question,
      displayOrder: index
    }));
    onReviewCriteriaChange(questions);
  };

  const getQuestionTypeLabel = (type) => {
    const option = questionTypeOptions?.find(opt => opt?.value === type);
    return option?.label || type;
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-muted/50 rounded-lg p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">{k('quickStart.title')}</h3>
            <p className="text-sm text-muted-foreground">
              {k('quickStart.subtitle')}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleUseDefaults}
            iconName="Zap"
            iconPosition="left"
          >
            {k('quickStart.button')}
          </Button>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">{k('addQuestionTitle')}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {k('addQuestionSubtitle')}
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label={k('questionText.label')}
                type="text"
                placeholder={k('questionText.placeholder')}
                value={newQuestion?.questionText}
                onChange={(e) => setNewQuestion({ ...newQuestion, questionText: e?.target?.value })}
              />
            </div>

            <Select
              label={k('questionType.label')}
              value={newQuestion?.questionType}
              onChange={(value) => setNewQuestion({ ...newQuestion, questionType: value })}
              options={questionTypeOptions}
            />

            <div className="flex items-center gap-2 pt-6">
              <Checkbox
                checked={newQuestion?.isRequired}
                onCheckedChange={(checked) => setNewQuestion({ ...newQuestion, isRequired: checked })}
              />
              <label className="text-sm text-foreground">{k('requiredQuestion')}</label>
            </div>
          </div>

          {newQuestion?.questionType === 'scale' && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
              <Input
                label={k('minValue')}
                type="number"
                value={newQuestion?.scaleMin}
                onChange={(e) => setNewQuestion({ ...newQuestion, scaleMin: parseInt(e?.target?.value) || 1 })}
              />
              <Input
                label={k('maxValue')}
                type="number"
                value={newQuestion?.scaleMax}
                onChange={(e) => setNewQuestion({ ...newQuestion, scaleMax: parseInt(e?.target?.value) || 5 })}
              />
              <Input
                label={k('minLabel.label')}
                type="text"
                placeholder={k('minLabel.placeholder')}
                value={newQuestion?.scaleMinLabel}
                onChange={(e) => setNewQuestion({ ...newQuestion, scaleMinLabel: e?.target?.value })}
              />
              <Input
                label={k('maxLabel.label')}
                type="text"
                placeholder={k('maxLabel.placeholder')}
                value={newQuestion?.scaleMaxLabel}
                onChange={(e) => setNewQuestion({ ...newQuestion, scaleMaxLabel: e?.target?.value })}
              />
            </div>
          )}

          <Input
            label={k('helpText.label')}
            type="text"
            placeholder={k('helpText.placeholder')}
            value={newQuestion?.helpText}
            onChange={(e) => setNewQuestion({ ...newQuestion, helpText: e?.target?.value })}
          />

          <Button
            onClick={handleAddQuestion}
            iconName="Plus"
            iconPosition="left"
            disabled={!newQuestion?.questionText?.trim()}
          >
            {k('addQuestion')}
          </Button>
        </div>
      </div>

      {reviewCriteria?.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base md:text-lg font-semibold text-foreground">
              {k('questionsTitle', { count: reviewCriteria?.length })}
            </h3>
          </div>

          <div className="space-y-3">
            {reviewCriteria?.map((question, index) => (
              <div
                key={question?.id}
                className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border border-border"
              >
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => handleMoveQuestion(index, 'up')}
                    disabled={index === 0}
                    className="p-1 hover:bg-background disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Icon name="ChevronUp" size={16} />
                  </button>
                  <button
                    onClick={() => handleMoveQuestion(index, 'down')}
                    disabled={index === reviewCriteria?.length - 1}
                    className="p-1 hover:bg-background disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Icon name="ChevronDown" size={16} />
                  </button>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{question?.questionText}</p>
                      {question?.isRequired && (
                        <span className="text-xs text-error">{k('required')}</span>
                      )}
                    </div>
                    <span className="text-xs px-2 py-1 bg-primary/10 text-primary">
                      {getQuestionTypeLabel(question?.questionType)}
                    </span>
                  </div>

                  {question?.helpText && (
                    <p className="text-sm text-muted-foreground mb-2">{question?.helpText}</p>
                  )}

                  {question?.questionType === 'scale' && (
                    <div className="text-xs text-muted-foreground">
                      {k('scale', {
                        min: question?.scaleMin,
                        minLabel: question?.scaleMinLabel ? `(${question?.scaleMinLabel})` : '',
                        max: question?.scaleMax,
                        maxLabel: question?.scaleMaxLabel ? `(${question?.scaleMaxLabel})` : ''
                      })}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handleRemoveQuestion(question?.id)}
                  className="p-2 hover:bg-error/10 text-error transition-colors"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-4 p-4 bg-muted/50 border border-border rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
              <div className="text-sm text-foreground">
                <p className="font-medium mb-1">{k('generalComments.title')}</p>
                <p className="text-muted-foreground">{k('generalComments.description')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {errors?.reviewCriteria && (
        <p className="text-sm text-error">{errors?.reviewCriteria}</p>
      )}
    </div>
  );
};

export default ReviewSchemaForm;
